import * as React from 'react';
import type { ReactNode } from 'react';
import { Tooltip as PrimeTooltip, type TooltipProps as PrimeTooltipProps } from 'primereact/tooltip';

type TooltipText = string | number;

export type PrimeTooltipHandle = InstanceType<typeof PrimeTooltip>;

function joinClassNames(...parts: (string | undefined)[]) {
  return parts.filter(Boolean).join(' ');
}

export type TooltipProps = Omit<PrimeTooltipProps, 'content'> & {
  /**
   * Backwards-compatible single content prop.
   * Ignored when `title` or `description` is provided, or when `richBody` / `footer` is used.
   */
  content?: TooltipText;
  /** Optional title line (bold). */
  title?: TooltipText;
  /** Optional description line. */
  description?: TooltipText;
  /** Rich tooltip body. When set (or `footer` is), it's rendered instead of `content`. */
  richBody?: ReactNode;
  /** Footer row inside the tooltip (e.g. “See more” + copy). Clicks require `autoHide={false}` (default when footer is present). */
  footer?: ReactNode;
};

export const Tooltip = React.forwardRef<PrimeTooltipHandle, TooltipProps>(function Tooltip(
  { title, description, content, richBody, footer, children: _children, autoHide, className, pt, onShow, ...rest },
  ref,
) {
  const hasRichSlot = richBody != null || footer != null;

  if (hasRichSlot) {
    const resolvedAutoHide = autoHide !== undefined ? autoHide : footer != null ? false : true;

    const existingTextClassName = (() => {
      const textPt = pt?.text;
      if (!textPt || typeof textPt === 'function') return undefined;
      const c = (textPt as { className?: unknown }).className;
      return typeof c === 'string' ? c : undefined;
    })();

    const textPt =
      typeof pt?.text === 'function'
        ? pt.text
        : {
            ...(typeof pt?.text === 'object' && pt?.text ? pt.text : {}),
            className: joinClassNames(
              existingTextClassName,
              // The body brings its own padding, so drop the theme's child padding or it pushes the body right.
              '!max-w-none whitespace-normal !p-0',
            ),
          };

    const mergedPt = {
      ...(pt ?? {}),
      text: textPt,
    } satisfies PrimeTooltipProps['pt'];

    const handleShow = (event: Parameters<NonNullable<PrimeTooltipProps['onShow']>>[0]) => {
      onShow?.(event);
    };

    return (
      <PrimeTooltip
        ref={ref}
        {...rest}
        autoHide={resolvedAutoHide}
        content={null}
        className={className}
        pt={mergedPt}
        onShow={handleShow}
      >
        {/* max-w-full keeps it inside the root's border instead of clipping its right edge. */}
        <div className="box-border w-100 max-w-full text-sm leading-5">
          {richBody}
          {footer != null ? footer : null}
        </div>
      </PrimeTooltip>
    );
  }

  const hasStructuredContent = title !== undefined || description !== undefined;

  if (!hasStructuredContent) {
    return (
      <PrimeTooltip
        ref={ref}
        {...rest}
        className={className}
        pt={pt}
        onShow={onShow}
        content={content as unknown as PrimeTooltipProps['content']}
      />
    );
  }

  const text = [title, description]
    .filter((x): x is TooltipText => x !== undefined)
    .map(String)
    .join('\n');

  const structuredTextClassName =
    'whitespace-pre-line break-words text-sm font-normal leading-5 first-line:font-semibold';

  const existingTextClassName = (() => {
    const textPt = pt?.text;
    if (!textPt || typeof textPt === 'function') return undefined;
    const c = (textPt as { className?: unknown }).className;
    return typeof c === 'string' ? c : undefined;
  })();

  const mergedPt = {
    ...(pt ?? {}),
    text: {
      ...(typeof pt?.text === 'object' && pt?.text ? pt.text : {}),
      className: joinClassNames(existingTextClassName, structuredTextClassName),
    },
  } satisfies PrimeTooltipProps['pt'];

  return (
    <PrimeTooltip
      ref={ref}
      {...rest}
      className={className}
      pt={mergedPt}
      onShow={onShow}
      content={text as unknown as PrimeTooltipProps['content']}
    />
  );
});

/**
 * Tooltip surface: white card with the theme border. `!` is needed to beat PrimeReact's classes.
 * Use the variant for the tooltip's `position`: `TOOLTIP_SURFACE` (top), `TOOLTIP_SURFACE_BOTTOM`,
 * `TOOLTIP_SURFACE_SIDE` (right), `TOOLTIP_SURFACE_SIDE_LEFT` (left).
 */
const TOOLTIP_SURFACE_BASE = '!bg-theme-white !border-theme-border-grey !text-theme-black';

export const TOOLTIP_SURFACE = `${TOOLTIP_SURFACE_BASE} !-translate-y-2.5`;

export const TOOLTIP_SURFACE_BOTTOM = `${TOOLTIP_SURFACE_BASE} !translate-y-2.5`;

export const TOOLTIP_SURFACE_SIDE = `${TOOLTIP_SURFACE_BASE} !translate-x-2.5`;

export const TOOLTIP_SURFACE_SIDE_LEFT = `${TOOLTIP_SURFACE_BASE} !-translate-x-2.5`;
